import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const CATEGORIES = [
  'All',
  'Disease Surveillance',
  'Public Health',
  'Vaccination',
  'Health Equity',
  'Epidemiology',
  'Data Analytics',
  'Policy & Compliance',
  'Technology',
  'Case Studies',
];

const CATEGORY_COLORS = {
  'Disease Surveillance': '#ef4444',
  'Public Health': '#3b82f6',
  'Vaccination': '#22c55e',
  'Health Equity': '#a855f7',
  'Epidemiology': '#f97316',
  'Data Analytics': '#0ea5e9',
  'Policy & Compliance': '#64748b',
  'Technology': '#8b5cf6',
  'Case Studies': '#eab308',
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

/* ─── Skeleton Card ─── */
const SkeletonCard = ({ cardBg, border, isDark }) => (
  <div style={{
    background: cardBg,
    border: `1px solid ${border}`,
    borderRadius: 16,
    padding: 24,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  }}>
    <div style={{ width: 80, height: 22, borderRadius: 20, background: isDark ? '#1e293b' : '#e2e8f0', animation: 'shimmer 1.5s infinite' }} />
    <div style={{ width: '90%', height: 20, borderRadius: 6, background: isDark ? '#1e293b' : '#e2e8f0', animation: 'shimmer 1.5s infinite' }} />
    <div style={{ width: '70%', height: 20, borderRadius: 6, background: isDark ? '#1e293b' : '#e2e8f0', animation: 'shimmer 1.5s infinite' }} />
    <div style={{ width: '100%', height: 60, borderRadius: 8, background: isDark ? '#1e293b' : '#e2e8f0', animation: 'shimmer 1.5s infinite' }} />
    <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
      <div style={{ width: 60, height: 14, borderRadius: 4, background: isDark ? '#1e293b' : '#e2e8f0' }} />
      <div style={{ width: 80, height: 14, borderRadius: 4, background: isDark ? '#1e293b' : '#e2e8f0' }} />
    </div>
  </div>
);

/* ─── Blog Card ─── */
const BlogCard = ({ post, cardBg, border, textPrimary, textSecondary, accent }) => {
  const catColor = CATEGORY_COLORS[post.category] || '#64748b';
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: cardBg,
        border: `1px solid ${hovered ? accent : border}`,
        borderRadius: 16,
        padding: 24,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        transition: 'all 0.3s ease',
        transform: hovered ? 'translateY(-4px)' : 'none',
        boxShadow: hovered ? '0 12px 40px rgba(0,212,170,0.08)' : 'none',
        cursor: 'default',
      }}
    >
      {/* Category badge */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{
          display: 'inline-block',
          padding: '4px 12px',
          borderRadius: 20,
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.5px',
          textTransform: 'uppercase',
          background: `${catColor}18`,
          color: catColor,
          border: `1px solid ${catColor}30`,
        }}>
          {post.category}
        </span>
        {post.readTime && (
          <span style={{ fontSize: 12, color: textSecondary }}>{post.readTime} min read</span>
        )}
      </div>

      {/* Title */}
      <h3 style={{
        fontSize: 18,
        fontWeight: 700,
        color: textPrimary,
        fontFamily: "'DM Serif Display', serif",
        lineHeight: 1.4,
        margin: 0,
      }}>
        {post.title}
      </h3>

      {/* Excerpt */}
      <p style={{
        fontSize: 14,
        color: textSecondary,
        lineHeight: 1.7,
        margin: 0,
        display: '-webkit-box',
        WebkitLineClamp: 3,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
      }}>
        {post.excerpt}
      </p>

      {/* Footer */}
      <div style={{ marginTop: 'auto', paddingTop: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: `1px solid ${border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'linear-gradient(135deg, #00d4aa, #00b894)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 9, color: '#020617', fontFamily: "'DM Serif Display', serif", flexShrink: 0 }}>ES</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: textPrimary }}>
            EpiSurveillance Editorial
          </div>
          <div style={{ fontSize: 12, color: textSecondary, marginTop: 2 }}>
            {formatDate(post.publishedAt || post.createdAt)}
          </div>
        </div>
        <Link
          to={`/blog/${post.slug}`}
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: accent,
            textDecoration: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            transition: 'gap 0.2s',
          }}
          onMouseEnter={e => e.currentTarget.style.gap = '8px'}
          onMouseLeave={e => e.currentTarget.style.gap = '4px'}
        >
          Read More →
        </Link>
      </div>
    </div>
  );
};

/* ─── MAIN COMPONENT ─── */
const BlogList = () => {
  const { isDark } = useTheme();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('All');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPosts, setTotalPosts] = useState(0);
  const [scrolled, setScrolled] = useState(false);
  const debounceRef = useRef(null);

  const c = {
    pageBg:      isDark ? '#020617' : '#f8fafc',
    cardBg:      isDark ? '#0f172a' : '#ffffff',
    border:      isDark ? '#1e293b' : '#e2e8f0',
    textPrimary: isDark ? '#f1f5f9' : '#0f172a',
    textSecondary: isDark ? '#94a3b8' : '#64748b',
    navBg:       isDark ? 'rgba(2,6,23,0.95)' : 'rgba(255,255,255,0.97)',
    navBorder:   isDark ? '#1e293b' : '#e2e8f0',
    inputBg:     isDark ? '#0f172a' : '#ffffff',
    inputBorder: isDark ? '#1e293b' : '#d1d5db',
    accent:      '#00d4aa',
    tabActiveBg: isDark ? 'rgba(0,212,170,0.1)' : 'rgba(0,212,170,0.08)',
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 9 });
      if (activeCategory !== 'All') params.set('category', activeCategory);
      if (search) params.set('search', search);
      const res = await fetch(`${API_BASE}/blog/public?${params}`);
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setPosts(data.posts || data.data || []);
      setTotalPages(data.totalPages || data.pagination?.totalPages || 1);
      setTotalPosts(data.total || data.pagination?.total || 0);
    } catch {
      setPosts([]);
    }
    setLoading(false);
  }, [page, activeCategory, search]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  // Debounce search input
  const handleSearchChange = (val) => {
    setSearchInput(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch(val);
      setPage(1);
    }, 400);
  };

  const handleCategoryChange = (cat) => {
    setActiveCategory(cat);
    setPage(1);
  };

  return (
    <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", background: c.pageBg, minHeight: '100vh', color: c.textPrimary }}>
      <style>{`
        * { box-sizing: border-box; }
        @keyframes shimmer { 0%, 100% { opacity: 0.6; } 50% { opacity: 1; } }
        @keyframes spin { to { transform: rotate(360deg); } }
        .blog-nav-link { color: ${c.textSecondary}; text-decoration: none; font-size: 14px; font-weight: 600; transition: color 0.2s; }
        .blog-nav-link:hover { color: #00d4aa; }
        .blog-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
        @media (max-width: 1024px) { .blog-grid { grid-template-columns: repeat(2, 1fr); } }
        @media (max-width: 640px) {
          .blog-grid { grid-template-columns: 1fr; }
          .blog-cats { flex-wrap: wrap; }
          .blog-header-title { font-size: 36px !important; }
        }
      `}</style>

      {/* ─── NAV ─── */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        height: 68,
        padding: '0 48px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: scrolled ? c.navBg : 'transparent',
        backdropFilter: scrolled ? 'blur(20px)' : 'none',
        borderBottom: scrolled ? `1px solid ${c.navBorder}` : 'none',
        transition: 'all 0.3s',
      }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
          <div style={{
            width: 38, height: 38, borderRadius: 10,
            background: 'linear-gradient(135deg, #00d4aa, #00b894)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 800, fontSize: 14, color: '#020617', fontFamily: "'DM Serif Display', serif",
          }}>ES</div>
          <span style={{ fontWeight: 800, fontSize: 17, color: c.textPrimary, letterSpacing: '-0.5px' }}>EpiSurveillance</span>
        </Link>
        <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
          <Link to="/login" className="blog-nav-link">Sign In</Link>
          <Link to="/register" style={{
            padding: '8px 20px', borderRadius: 10,
            background: 'linear-gradient(135deg, #00d4aa, #00b894)',
            color: '#020617', fontWeight: 700, fontSize: 14,
            textDecoration: 'none', transition: 'opacity 0.2s',
          }}
            onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
            onMouseLeave={e => e.currentTarget.style.opacity = '1'}
          >
            Get Started
          </Link>
        </div>
      </nav>

      {/* ─── PAGE HEADER ─── */}
      <section style={{ paddingTop: 120, paddingBottom: 60, textAlign: 'center', padding: '120px 48px 60px' }}>
        <div style={{
          display: 'inline-block', padding: '6px 16px', borderRadius: 20,
          background: 'rgba(0,212,170,0.1)', border: '1px solid rgba(0,212,170,0.25)',
          color: '#00d4aa', fontSize: 12, fontWeight: 700, letterSpacing: '1px',
          textTransform: 'uppercase', marginBottom: 20,
        }}>
          EpiSurveillance Blog
        </div>
        <h1 className="blog-header-title" style={{
          fontSize: 52, fontWeight: 800, fontFamily: "'DM Serif Display', serif",
          color: c.textPrimary, lineHeight: 1.2, marginBottom: 16, marginTop: 0,
        }}>
          Insights on Public Health
        </h1>
        <p style={{ fontSize: 18, color: c.textSecondary, maxWidth: 560, margin: '0 auto' }}>
          Insights on public health surveillance, epidemiology, and AI
        </p>
      </section>

      {/* ─── FILTERS ─── */}
      <section style={{ padding: '0 48px 40px', maxWidth: 1280, margin: '0 auto' }}>
        {/* Search */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 28 }}>
          <div style={{ position: 'relative', width: '100%', maxWidth: 480 }}>
            <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: c.textSecondary, fontSize: 16 }}>🔍</span>
            <input
              type="text"
              placeholder="Search articles..."
              value={searchInput}
              onChange={e => handleSearchChange(e.target.value)}
              style={{
                width: '100%', padding: '12px 16px 12px 40px',
                borderRadius: 12, border: `1px solid ${c.inputBorder}`,
                background: c.inputBg, color: c.textPrimary,
                fontSize: 14, outline: 'none',
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                transition: 'border-color 0.2s',
              }}
              onFocus={e => e.target.style.borderColor = '#00d4aa'}
              onBlur={e => e.target.style.borderColor = c.inputBorder}
            />
          </div>
        </div>

        {/* Category tabs */}
        <div className="blog-cats" style={{
          display: 'flex', gap: 8, overflowX: 'auto',
          paddingBottom: 4, justifyContent: 'center', flexWrap: 'wrap',
        }}>
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              onClick={() => handleCategoryChange(cat)}
              style={{
                padding: '8px 18px', borderRadius: 20, border: 'none',
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: 13, fontWeight: 600, cursor: 'pointer',
                whiteSpace: 'nowrap', transition: 'all 0.2s',
                background: activeCategory === cat ? c.accent : (isDark ? '#1e293b' : '#f1f5f9'),
                color: activeCategory === cat ? '#020617' : c.textSecondary,
                boxShadow: activeCategory === cat ? '0 4px 12px rgba(0,212,170,0.25)' : 'none',
              }}
            >
              {cat}
            </button>
          ))}
        </div>
      </section>

      {/* ─── MAIN CONTENT ─── */}
      <section style={{ padding: '0 48px 80px', maxWidth: 1280, margin: '0 auto' }}>
        {totalPosts > 0 && !loading && (
          <p style={{ color: c.textSecondary, fontSize: 13, marginBottom: 24 }}>
            {totalPosts} article{totalPosts !== 1 ? 's' : ''} found
            {activeCategory !== 'All' ? ` in "${activeCategory}"` : ''}
            {search ? ` for "${search}"` : ''}
          </p>
        )}

        {/* Loading skeletons */}
        {loading && (
          <div className="blog-grid">
            {Array.from({ length: 9 }).map((_, i) => (
              <SkeletonCard key={i} cardBg={c.cardBg} border={c.border} isDark={isDark} />
            ))}
          </div>
        )}

        {/* Posts grid */}
        {!loading && posts.length > 0 && (
          <div className="blog-grid">
            {posts.map(post => (
              <BlogCard
                key={post._id || post.slug}
                post={post}
                cardBg={c.cardBg}
                border={c.border}
                textPrimary={c.textPrimary}
                textSecondary={c.textSecondary}
                accent={c.accent}
              />
            ))}
          </div>
        )}

        {/* Empty state */}
        {!loading && posts.length === 0 && (
          <div style={{ textAlign: 'center', padding: '80px 24px' }}>
            <div style={{ fontSize: 56, marginBottom: 20 }}>📭</div>
            <h3 style={{ fontSize: 22, fontWeight: 700, color: c.textPrimary, marginBottom: 10, fontFamily: "'DM Serif Display', serif" }}>
              No articles found
            </h3>
            <p style={{ color: c.textSecondary, fontSize: 15 }}>
              {search
                ? `No results for "${search}". Try a different search term.`
                : activeCategory !== 'All'
                  ? `No articles in "${activeCategory}" yet. Check back soon.`
                  : 'No blog posts have been published yet. Check back soon.'
              }
            </p>
            {(search || activeCategory !== 'All') && (
              <button
                onClick={() => { setSearchInput(''); setSearch(''); setActiveCategory('All'); setPage(1); }}
                style={{
                  marginTop: 20, padding: '10px 24px', borderRadius: 10,
                  background: c.accent, color: '#020617',
                  border: 'none', fontWeight: 700, fontSize: 14,
                  cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif",
                }}
              >
                Clear Filters
              </button>
            )}
          </div>
        )}

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginTop: 48 }}>
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              style={{
                padding: '10px 22px', borderRadius: 10,
                border: `1px solid ${c.border}`,
                background: page === 1 ? 'transparent' : c.cardBg,
                color: page === 1 ? c.textSecondary : c.textPrimary,
                fontWeight: 600, fontSize: 14, cursor: page === 1 ? 'not-allowed' : 'pointer',
                fontFamily: "'Plus Jakarta Sans', sans-serif", transition: 'all 0.2s',
                opacity: page === 1 ? 0.5 : 1,
              }}
            >
              ← Previous
            </button>
            <div style={{ display: 'flex', gap: 6 }}>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                .reduce((acc, p, idx, arr) => {
                  if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                  acc.push(p);
                  return acc;
                }, [])
                .map((item, idx) =>
                  item === '...'
                    ? <span key={`dot-${idx}`} style={{ color: c.textSecondary, padding: '0 4px', lineHeight: '38px' }}>…</span>
                    : (
                      <button
                        key={item}
                        onClick={() => setPage(item)}
                        style={{
                          width: 38, height: 38, borderRadius: 8,
                          border: `1px solid ${item === page ? c.accent : c.border}`,
                          background: item === page ? c.accent : 'transparent',
                          color: item === page ? '#020617' : c.textPrimary,
                          fontWeight: 600, fontSize: 14, cursor: 'pointer',
                          fontFamily: "'Plus Jakarta Sans', sans-serif",
                          transition: 'all 0.2s',
                        }}
                      >
                        {item}
                      </button>
                    )
                )}
            </div>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              style={{
                padding: '10px 22px', borderRadius: 10,
                border: `1px solid ${c.border}`,
                background: page === totalPages ? 'transparent' : c.cardBg,
                color: page === totalPages ? c.textSecondary : c.textPrimary,
                fontWeight: 600, fontSize: 14, cursor: page === totalPages ? 'not-allowed' : 'pointer',
                fontFamily: "'Plus Jakarta Sans', sans-serif", transition: 'all 0.2s',
                opacity: page === totalPages ? 0.5 : 1,
              }}
            >
              Next →
            </button>
          </div>
        )}
      </section>

      {/* ─── FOOTER ─── */}
      <footer style={{
        borderTop: `1px solid ${c.border}`,
        padding: '32px 48px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: isDark ? '#020617' : '#f8fafc',
      }}>
        <Link to="/" style={{
          display: 'flex', alignItems: 'center', gap: 8,
          color: c.accent, textDecoration: 'none', fontWeight: 600, fontSize: 14,
          transition: 'opacity 0.2s',
        }}
          onMouseEnter={e => e.currentTarget.style.opacity = '0.8'}
          onMouseLeave={e => e.currentTarget.style.opacity = '1'}
        >
          ← Back to Home
        </Link>
        <p style={{ color: c.textSecondary, fontSize: 13, margin: 0 }}>
          © {new Date().getFullYear()} EpiSurveillance. All rights reserved.
        </p>
      </footer>
    </div>
  );
};

export default BlogList;
